import { Box } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { deDE } from '@mui/x-data-grid/locales';
import { get } from 'lodash';
import React, { FC, useMemo } from 'react';
import { useApplication } from 'src/context/ApplicationContext';
import { KOSTEN_ARTEN, PLATZHALTER_TEXT } from 'src/lib/constants/constant';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import { DisplayEuroMUI } from 'src/utils/formats';
import { isTrue } from 'src/utils/helper';

// import schemas
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
import kostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
import { getUnternehmenDisplayName } from 'src/utils/ausgabenFinanzierungHelper';
const P = Schema.attributes;
const KP_P = kostenPositionSchema.attributes;

const PersonalKostenUebersichtAntragLoewe: FC<{}> = () => {
	const { application } = useApplication();
	const columns = useMemo(() => {
		const projectStartYear = get(application, P.Allgemein_Projektstart_Jahr.path);
		const projectDurationYears = get(application, P.Allgemein_Projektdauerin_Jahren.path);

		let column = [
			{ field: 'unternehmen', headerName: 'Organisation', width: 200 },
			{ field: 'name', headerName: 'Name', minWidth: 120, flex: 1, groupable: false },
			{
				field: 'funktion',
				headerName: 'Funktion im geplanten Projekt',
				flex: 1,
				minWidth: 140,
				renderHeader: () => (
					<Box sx={{ lineHeight: 1.2, fontWeight: '500', width: '100%' }}>
						<Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>Funktionen im</Box>
						<Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>geplanten Projekt</Box>
					</Box>
				),
			},
			...Array.from({ length: projectDurationYears }).map((_, i) => {
				const year = projectStartYear + i;
				return {
					field: `stundensatz_${year}`,
					headerName: `Stundensatz ${year}`,
					minWidth: 100,
					type: 'number' as const,
					renderHeader: () => (
						<Box sx={{ whiteSpace: 'normal', lineHeight: 1.2, fontWeight: '500', textAlign: 'right' }}>
							Stundensatz
							<br />
							{year}
						</Box>
					),
					valueFormatter: (value: any) => {
						return value || value === 0 ? DisplayEuroMUI(value) : null;
					},
				};
			}),
			...Array.from({ length: projectDurationYears }).map((_, i) => {
				const year = projectStartYear + i;
				return {
					field: `${year}`,
					headerName: `Personalausgaben ${year}`,
					minWidth: 130,
					type: 'number' as const,
					renderHeader: () => (
						<Box sx={{ whiteSpace: 'normal', lineHeight: 1.2, fontWeight: '500', textAlign: 'right' }}>
							Personalausgaben
							<br />
							{year}
						</Box>
					),
					valueFormatter: (value: any) => {
						return DisplayEuroMUI(value);
					},
				};
			}),
			{
				field: 'geplanteSumme',
				headerName: 'Geplante Summe',
				minWidth: 140,
				type: 'number' as const,
				valueFormatter: (value: any) => {
					return DisplayEuroMUI(value);
				},
			},
		];

		return column;
	}, [application]);

	const rows = useMemo(() => {
		let row = get(application, P.Foerderantrag_Kostenpositions.path, [])
			.filter((ele: any) => get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Personalkosten)
			.map((ele: any) => {
				const loeweProjektJahre = get(ele, KP_P.Personalausgaben_LOEWE_Antrag.schema.attributes.Loewe_Antrag_Projekt_Jahr.path, []);
				const stundensatzProJahr = loeweProjektJahre.reduce((acc: any, jahr: any) => {
					const year = get(jahr, 'Zelle_1');
					if (year) acc[`stundensatz_${year}`] = Number(get(jahr, 'Zelle_11', 0));
					return acc;
				}, {});
				const kostenProJahr = get(ele, KP_P.Allgemein_Kosten_Pro_Jahr.path, []).reduce((acc: any, proJahr: any) => {
					acc[get(proJahr, 'Zelle_1')] = get(proJahr, 'Zelle_2');
					return acc;
				}, {});
				return {
					id: get(ele, 'id'),
					kostraeger: get(ele, KP_P.Allgemein_Kostentraeger.path, ''),
					unternehmen: getUnternehmenDisplayName(application, ele),
					name: get(ele, KP_P.Personalausgaben_LOEWE_Antrag.schema.attributes.Name_Desder_Beschaeftigten.path),
					funktion: get(ele, KP_P.Personalausgaben_LOEWE_Antrag.schema.attributes.Funktion_Im_Beantragten_Vorhaben.path, ''),
					...stundensatzProJahr,
					kostenart: get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path),
					kostenKategorie: get(ele, KP_P.Allgemein_Kostenkategorie.schema.attributes.Allgemein_Bezeichnung.path),
					...kostenProJahr,
					geplanteSumme: get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path),
					isAdminPauschale: isTrue(get(ele, KP_P.Personalausgaben_LOEWE_Antrag.schema.attributes.Administrationspauschale.path, false)),
				};
			})
			.sort((a: any, b: any) => {
				// Innerhalb der gleichen Gruppe (unternehmen): Administrationspauschale immer am Ende
				// Administrationspauschale ist die 20% Pauschale für HAW, die im Akkordion nicht angezeigt werden müssen.
				// Das wird per Formel ausgerechnet bzw. angelegt, wenn die Antragstellende ein HAW ist.
				if (a.unternehmen === b.unternehmen) {
					return Number(a.isAdminPauschale) - Number(b.isAdminPauschale);
				}
				return 0;
			});
		return row;
	}, [application]);

	const initialState: any = useMemo(() => {
		const projectStartYear = get(application, P.Allgemein_Projektstart_Jahr.path);
		const projectDurationYears = get(application, P.Allgemein_Projektdauerin_Jahren.path);

		const model: any = Array.from({ length: projectDurationYears }).reduce((acc: any, _, i) => {
			acc[projectStartYear + i] = 'sum';
			return acc;
		}, {});

		return {
			aggregation: {
				model: {
					...model,
					geplanteSumme: 'sum',
				},
			},
			columns: {
				columnVisibilityModel: {
					// Hide the column used for leaves
					unternehmen: false,
				},
			},
			rowGrouping: {
				model: ['unternehmen'],
			},
		};
	}, [application]);

	return (
		<>
			<ApplicationHeading title='Personalausgaben Übersicht' description={PLATZHALTER_TEXT} />
			<Box sx={{ height: 'auto', width: '100%' }}>
				<DataGridPremium
					rows={rows}
					columns={columns}
					columnHeaderHeight={72}
					initialState={initialState}
					groupingColDef={{
						headerName: 'Organisation',
						valueFormatter: (value: any) => value ?? 'Gesamt',
					}}
					hideFooter
					disableRowSelectionOnClick
					localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
				/>
			</Box>
		</>
	);
};
export default PersonalKostenUebersichtAntragLoewe;
